/**
 * Activity typeahead — substring search across the schedule's activities by
 * task_code AND task_name. Dropdown rendered with position: fixed so it
 * escapes the table cell's overflow:hidden.
 *
 * Excludes the focal activity (no self-loop) and any predIds already in use
 * for this focal (no duplicate add — the backend rejects this anyway, but
 * UX-wise we hide them from the picker).
 */
import React from "react";
import type { LogicActivity } from "../../../../../models";
import "./ActivityAutocomplete.css";

interface Props {
  activityById: Map<number, LogicActivity>;
  excludeIds: Set<number>;
  onPick: (activity: LogicActivity) => void;
  onCancel: () => void;
  autoFocus?: boolean;
  placeholder?: string;
}

const MAX_RESULTS = 8;

const ActivityAutocomplete: React.FC<Props> = ({
  activityById,
  excludeIds,
  onPick,
  onCancel,
  autoFocus = true,
  placeholder = "Search activities…",
}) => {
  const [query, setQuery] = React.useState("");
  const [highlighted, setHighlighted] = React.useState(0);
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  const matches = React.useMemo(() => {
    if (query.trim().length === 0) return [];
    const q = query.trim().toLowerCase();
    const out: LogicActivity[] = [];
    activityById.forEach((a) => {
      if (excludeIds.has(a.id)) return;
      if (
        a.task_code.toLowerCase().includes(q) ||
        a.task_name.toLowerCase().includes(q)
      ) {
        out.push(a);
      }
      // No early break — total set is small enough that we just sort+truncate
    });
    out.sort((a, b) => a.task_code.localeCompare(b.task_code));
    return out.slice(0, MAX_RESULTS);
  }, [query, activityById, excludeIds]);

  React.useEffect(() => {
    setHighlighted(0);
  }, [matches.length]);

  React.useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Update dropdown anchor on scroll/resize. We only attach listeners while
  // the dropdown is actually visible (matches.length > 0) so we don't pay the
  // capture-phase scroll cost on every page scroll when nothing is shown.
  // rAF-throttled to coalesce rapid scroll events into one per frame.
  const updateAnchor = React.useCallback(() => {
    if (inputRef.current) {
      setAnchorRect(inputRef.current.getBoundingClientRect());
    }
  }, []);
  const dropdownVisible = matches.length > 0;
  React.useEffect(() => {
    updateAnchor();
    if (!dropdownVisible) return;
    let frame: number | null = null;
    const handler = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        updateAnchor();
      });
    };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [updateAnchor, dropdownVisible]);

  // Close on outside click. Using a capture-phase listener so a click anywhere
  // (including inside another row's editor) cancels.
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || dropdownRef.current?.contains(t)) {
        return;
      }
      onCancel();
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [onCancel]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
      return;
    }
    if (matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      onPick(matches[highlighted]);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        className="logic-activity-autocomplete-input"
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {matches.length > 0 && anchorRect && (
        <div
          ref={dropdownRef}
          className="logic-activity-autocomplete-dropdown"
          style={{
            top: anchorRect.bottom + 2,
            left: anchorRect.left,
            minWidth: anchorRect.width,
          }}
        >
          {matches.map((m, idx) => (
            <div
              key={m.id}
              className={`logic-activity-autocomplete-item ${
                idx === highlighted ? "highlighted" : ""
              }`}
              onMouseEnter={() => setHighlighted(idx)}
              onMouseDown={(e) => {
                // mouseDown so we fire before the input's blur/outside-click.
                e.preventDefault();
                onPick(m);
              }}
            >
              <span className="logic-activity-autocomplete-code">{m.task_code}</span>
              <span className="logic-activity-autocomplete-name">{m.task_name}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ActivityAutocomplete;
